import { Tool } from '@goat-sdk/core';
import { EVMTransaction, EVMWalletClient } from '@goat-sdk/wallet-evm';
import axios from 'axios';
import { ERC20_ABI } from './abi/erc20';
import { getChainId } from './chain-mapping';
import { GetQuoteParameters } from './parameters';
import WagmiWalletClient from 'mode-shared/clients/WagmiWalletClient';

const API_URL = 'https://li.quest/v1';

export class LifiService {
  constructor(private readonly apiKey?: string) {}

  private async _address(walletClient: EVMWalletClient): Promise<string> {
    if ((walletClient as WagmiWalletClient).isWagmi) {
      return await (walletClient as WagmiWalletClient).getAddressAsync();
    }

    return walletClient.getAddress();
  }

  @Tool({
    name: 'lifi_get_quote',
    description: 'Get a quote for a cross-chain token transfer using the LiFi API',
  })
  async getQuote(walletClient: EVMWalletClient, parameters: GetQuoteParameters) {
    const { fromChain, toChain, fromToken, toToken, fromAmount } = parameters;

    // Convert chain names to chain IDs
    const fromChainId = getChainId(fromChain);
    const toChainId = getChainId(toChain);

    const requestParams = {
      fromChain: fromChainId,
      toChain: toChainId,
      fromToken,
      toToken,
      fromAmount,
      fromAddress: await this._address(walletClient),
      slippage: 1,
    };

    try {
      const response = await axios.get(`${API_URL}/quote`, {
        params: requestParams,
        headers: this.apiKey
          ? {
              'x-lifi-api-key': this.apiKey,
            }
          : undefined,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.message === 'No available quotes for the requested transfer') {
          return {
            error: true,
            message: `No bridge routes available for ${fromToken} from ${fromChain} to ${toToken} on ${toChain}. Please try a different token pair or chain combination.`,
          };
        }
        return {
          error: true,
          message: `LiFi API error: ${error.response?.data?.message || error.message}`,
        };
      }

      return {
        error: true,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  @Tool({
    name: 'lifi_bridge',
    description:
      'Bridge tokens across chains using the LiFi protocol, call this lifi_bridge tool directly when users want to bridge. Approvals are also handled automatically.',
  })
  async bridge(walletClient: EVMWalletClient, parameters: GetQuoteParameters) {
    try {
      // First get the quote
      const quoteResponse = await this.getQuote(walletClient, parameters);

      // Check if there was an error finding routes
      if (quoteResponse.error) {
        return quoteResponse;
      }

      // Get the transaction request from the quote
      const { transactionRequest, action } = quoteResponse;

      const finalTxRequest: EVMTransaction = {
        to: transactionRequest.to,
        data: transactionRequest.data,
      };

      const amount = BigInt(parameters.fromAmount ?? '0');

      const isEth = action.fromToken.address === '0x0000000000000000000000000000000000000000';

      if (!isEth) {
        const currentAllowance = await walletClient.read({
          address: action.fromToken.address,
          abi: ERC20_ABI,
          functionName: 'allowance',
          args: [await this._address(walletClient), transactionRequest.to],
        });

        if (!currentAllowance || currentAllowance.value === undefined) {
          return 'Allowance value is undefined';
        }
        const allowance = BigInt(currentAllowance.value as string);

        if (allowance < amount) {
          await walletClient.sendTransaction({
            to: action.fromToken.address,
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [transactionRequest.to, amount],
          });
        }
      } else {
        finalTxRequest.value = amount;
      }

      // Execute the bridge transaction

      const txResponse = await walletClient.sendTransaction(finalTxRequest);

      return txResponse;
    } catch (error) {
      return error instanceof Error ? error.message : 'Unknown error';
    }
  }
}
